import React, { useState, useEffect } from 'react';
import { Mail, Plus, Edit2, Trash2, Save, X, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const EmailRedirectPanel = () => {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    source_email: '',
    destination_email: '',
    description: '',
    is_active: true
  });

  // SEC-002: cookie-based admin auth — no browser-side key needed.

  // Fetch redirects
  const fetchRedirects = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-redirects`, {
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to fetch redirects');
      
      const data = await response.json();
      setRedirects(data.redirects || []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load email redirects');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRedirects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create redirect
  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-redirects`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create redirect');
      }

      toast.success('Email redirect created successfully');
      setShowAddForm(false);
      setFormData({ source_email: '', destination_email: '', description: '', is_active: true });
      fetchRedirects();
    } catch (error) {
      toast.error(error.message || 'Failed to create redirect');
    }
  };

  // Update redirect
  const handleUpdate = async (redirectId, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-redirects/${redirectId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update redirect');

      toast.success('Email redirect updated successfully');
      setEditingId(null);
      fetchRedirects();
    } catch (error) {
      toast.error('Failed to update redirect');
    }
  };

  // Delete redirect
  const handleDelete = async (redirectId) => {
    if (!window.confirm('Are you sure you want to delete this email redirect?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-redirects/${redirectId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete redirect');

      toast.success('Email redirect deleted successfully');
      fetchRedirects();
    } catch (error) {
      toast.error('Failed to delete redirect');
    }
  };

  // Seed default redirects
  const handleSeedDefaults = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-redirects/seed-defaults`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to seed defaults');

      const data = await response.json();
      toast.success(data.message);
      fetchRedirects();
    } catch (error) {
      toast.error('Failed to seed default redirects');
    }
  };

  // Toggle active status
  const toggleActive = async (redirect) => {
    await handleUpdate(redirect.id, { is_active: !redirect.is_active });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Email Redirect Management</h1>
              <p className="text-gray-600">Configure email forwarding for @winhousetutors.com addresses</p>
            </div>
          </div>
          <div className="flex gap-2">
            {redirects.length === 0 && (
              <button
                onClick={handleSeedDefaults}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Seed Defaults
              </button>
            )}
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Redirect
            </button>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">📧 How Email Redirects Work</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Set up forwarding from your @winhousetutors.com email to any personal email</li>
            <li>• Activate/deactivate redirects anytime without losing configuration</li>
            <li>• Update destination email addresses as needed</li>
            <li>• Track forwarding statistics for each redirect</li>
          </ul>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white border rounded-lg p-6 mb-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Add New Email Redirect</h3>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Source Email *</label>
                <input
                  type="email"
                  required
                  value={formData.source_email}
                  onChange={(e) => setFormData({ ...formData, source_email: e.target.value })}
                  placeholder="name@winhousetutors.com"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Destination Email *</label>
                <input
                  type="email"
                  required
                  value={formData.destination_email}
                  onChange={(e) => setFormData({ ...formData, destination_email: e.target.value })}
                  placeholder="your.personal@gmail.com"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description (Optional)</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Tyra's personal email redirect"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Activate immediately</label>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Create Redirect
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData({ source_email: '', destination_email: '', description: '', is_active: true });
                }}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Redirects List */}
      {redirects.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Email Redirects Yet</h3>
          <p className="text-gray-600 mb-4">Create your first redirect or seed default ones for tyra@ and steve@</p>
          <button
            onClick={handleSeedDefaults}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Seed Default Redirects
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {redirects.map((redirect) => (
            <RedirectCard
              key={redirect.id}
              redirect={redirect}
              isEditing={editingId === redirect.id}
              onEdit={() => setEditingId(redirect.id)}
              onCancelEdit={() => setEditingId(null)}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onToggleActive={toggleActive}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Redirect Card Component
const RedirectCard = ({ redirect, isEditing, onEdit, onCancelEdit, onUpdate, onDelete, onToggleActive }) => {
  const [editForm, setEditForm] = useState({
    destination_email: redirect.destination_email,
    description: redirect.description || '',
    is_active: redirect.is_active
  });

  const handleSave = () => {
    onUpdate(redirect.id, editForm);
  };

  return (
    <div className={`bg-white border rounded-lg p-6 shadow-sm ${!redirect.is_active && 'opacity-60'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Source Email (Read-only)</label>
                <input
                  type="text"
                  value={redirect.source_email}
                  disabled
                  className="w-full px-4 py-2 border rounded-lg bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Destination Email</label>
                <input
                  type="email"
                  value={editForm.destination_email}
                  onChange={(e) => setEditForm({ ...editForm, destination_email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
                <button
                  onClick={onCancelEdit}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-lg font-semibold">{redirect.source_email}</h3>
                {redirect.is_active ? (
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Active
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full flex items-center gap-1">
                    <XCircle className="w-3 h-3" />
                    Inactive
                  </span>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Forwards to:</span>
                  <span className="font-medium text-primary">{redirect.destination_email}</span>
                </div>
                {redirect.description && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Description:</span>
                    <span>{redirect.description}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-500">
                  <span>Emails forwarded:</span>
                  <span className="font-semibold">{redirect.forward_count || 0}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {!isEditing && (
          <div className="flex gap-2 ml-4">
            <button
              onClick={() => onToggleActive(redirect)}
              className={`p-2 rounded-lg transition-colors ${
                redirect.is_active
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  : 'bg-green-100 hover:bg-green-200 text-green-700'
              }`}
              title={redirect.is_active ? 'Deactivate' : 'Activate'}
            >
              {redirect.is_active ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            </button>
            <button
              onClick={onEdit}
              className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
              title="Edit"
            >
              <Edit2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => onDelete(redirect.id)}
              className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailRedirectPanel;
